import { useState, useEffect } from "react";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { useSubscription } from "../hooks/useSubscription";
import { usePlanState } from "../hooks/usePlanState";
import { fetchUsage } from "../lib/billingApi";
import { effectivePlanLabel } from "../utils/tier";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function FormBuilder() {
    const [forms, setForms] = useState([]);
    const [fetching, setFetching] = useState(true);
    const { getAllForms, deleteForm, updateForm, getSettings, error } = useFormBuilder();
    const { subsInfo } = useSubscription();
    const { effectivePlan, planState } = usePlanState();
    const [usage, setUsage] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchUsage().then(setUsage).catch(() => {});
    }, []);

    const APP_URL =
        process.env.REACT_APP_URL || process.env.REACT_APP_API_URL
            ? process.env.REACT_APP_API_URL?.replace("api.", "platform.")
            : "http://localhost:3000";

    // Verified custom domain takes precedence over the default platform domain for form links.
    const [customDomain, setCustomDomain] = useState("");
    const formBaseUrl = customDomain ? `https://${customDomain}` : APP_URL;

    useEffect(() => {
        loadForms();
        loadDomain();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadDomain = async () => {
        const data = await getSettings();
        if (data?.customDomain?.domain && data.customDomain.verified) {
            setCustomDomain(data.customDomain.domain);
        }
    };

    const loadForms = async () => {
        setFetching(true);
        const data = await getAllForms();
        setForms(data || []);
        setFetching(false);
    };

    const handleDelete = async (formId) => {
        if (!window.confirm("Delete this form and all its responses?")) return;
        const result = await deleteForm(formId);
        if (result) {
            setForms(forms.filter((f) => f.formId !== formId));
            toast("Form deleted");
        }
    };

    const handleTogglePublish = async (formId, currentStatus) => {
        const result = await updateForm(formId, {
            isPublished: !currentStatus,
        });
        if (result) {
            setForms(
                forms.map((f) =>
                    f.formId === formId
                        ? { ...f, isPublished: !currentStatus }
                        : f
                )
            );
            toast(currentStatus ? "Form unpublished" : "Form published!");
        }
    };

    const [editingTitle, setEditingTitle] = useState(null);
    const [editTitleValue, setEditTitleValue] = useState("");

    const copyLink = (formId) => {
        navigator.clipboard.writeText(`${formBaseUrl}/f/${formId}`);
        toast(customDomain ? "Link copied (custom domain)!" : "Link copied!");
    };

    const copyEmbed = (formId) => {
        const embedCode = `<iframe src="${formBaseUrl}/f/${formId}" width="100%" height="600" frameborder="0" style="border:none;max-width:700px;"></iframe>`;
        navigator.clipboard.writeText(embedCode);
        toast("Embed code copied!");
    };

    const handleTitleEdit = (form) => {
        setEditingTitle(form.formId);
        setEditTitleValue(form.title);
    };

    const handleTitleSave = async (formId) => {
        const trimmed = editTitleValue.trim();
        if (!trimmed) return;
        const result = await updateForm(formId, { title: trimmed });
        if (result) {
            setForms(forms.map((f) => f.formId === formId ? { ...f, title: trimmed } : f));
            toast("Title updated");
        }
        setEditingTitle(null);
    };

    const handleTitleKeyDown = (e, formId) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleTitleSave(formId);
        } else if (e.key === "Escape") {
            setEditingTitle(null);
        }
    };

    const formsUsed = subsInfo?.details?.forms || 0;
    const rawFormsLimit = subsInfo?.details?.totalforms;
    const unlimitedForms = !rawFormsLimit || rawFormsLimit >= 999999;
    // Show the real plan/tier label. For LTD buyers this is "Lifetime Tier N" — the
    // effective_plan slug maps a lifetime tier onto a SaaS slug (e.g. "starter") only so
    // legacy readers have a value, but showing that undersells what they bought. See
    // utils/tier.js effectivePlanLabel. LTD labels already read as a noun ("Lifetime
    // Tier 1"), so we don't append "plan" to them.
    const planBadge = planState?.has_ltd
        ? effectivePlanLabel(planState)
        : `${effectivePlanLabel(planState) || "Free"} plan`;
    // Monthly responses come from the usage meter (plan limit + any capacity boosters), so
    // purchased add-ons are reflected here. Fall back to the legacy field before usage loads.
    const respMeter = usage?.meters?.responses;
    const responsesUsed = respMeter?.used ?? 0;
    const responsesUnlimited = respMeter ? respMeter.unlimited : ((subsInfo?.details?.responsesPerForm ?? 0) === 0);
    const responsesLimit = respMeter && !respMeter.unlimited ? respMeter.limit : (subsInfo?.details?.responsesPerForm ?? 0);
    // Effective Agent Action workflows per form (plan limit + purchased boosters); -1 = unlimited, 0 = not on plan.
    const agentWf = planState?.agent_workflows_per_form ?? 0;
    const usagePercent = unlimitedForms ? 0 : Math.min((formsUsed / rawFormsLimit) * 100, 100);

    return (
        <div className="min-h-screen lg:w-3/4 md:w-3/4 sm:w-3/4 w-2/3 mx-auto font-Rajdhani">
            {/* Header with gradient accent */}
            <div className="mt-5 mb-6">
                <div className="h-1 w-20 bg-gradient-to-r from-indigo-600 to-purple-600 rounded mb-4" />
                <div className="flex flex-wrap justify-between items-start gap-3">
                    <div>
                        <h1 className="text-3xl font-bold">Form Builder</h1>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mt-1">
                            Create, customize, and share forms powered by AI
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate("/form-builder/settings")}
                            className="px-4 py-2 rounded-lg border-[1px] dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-[#2c2f44] text-sm font-medium"
                        >
                            Settings
                        </button>
                        <button
                            onClick={() => navigate("/form-builder/create")}
                            className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-indigo-600 to-indigo-500 dark:bg-[#818998] hover:opacity-90 text-sm font-semibold"
                        >
                            + Create New Form
                        </button>
                    </div>
                </div>

                {/* Usage Progress */}
                <div className="mt-4 p-3 border-[1px] dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#23263a]">
                    <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">
                            {formsUsed}
                            {unlimitedForms ? "" : ` / ${rawFormsLimit}`} form{formsUsed === 1 ? "" : "s"}
                            <span className="text-gray-400 dark:text-gray-500 ml-3">
                                {responsesUnlimited
                                    ? "Unlimited responses/mo"
                                    : `${responsesUsed.toLocaleString()} / ${responsesLimit.toLocaleString()} responses/mo`}
                            </span>
                            {agentWf !== 0 && (
                                <span className="text-gray-400 dark:text-gray-500 ml-3">
                                    {agentWf === -1
                                        ? "Unlimited agent actions/form"
                                        : `${agentWf} agent action${agentWf === 1 ? "" : "s"}/form`}
                                </span>
                            )}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                            {planBadge}
                        </span>
                    </div>
                    {!unlimitedForms && (
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-indigo-600 h-2 rounded-full transition-all"
                                style={{ width: `${usagePercent}%` }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {fetching ? (
                <div className="space-y-4">
                    {[1, 2].map((i) => (
                        <div key={i} className="border-[1px] dark:border-gray-700 rounded-lg p-4 animate-pulse">
                            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2" />
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32" />
                        </div>
                    ))}
                </div>
            ) : forms.length === 0 ? (
                <div className="text-center py-20 border-[1px] dark:border-gray-700 rounded-lg  dark:bg-[#23263a]">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <p className="text-xl text-gray-500 dark:text-gray-400 mb-2">No forms yet</p>
                    <p className="text-gray-400 dark:text-gray-500 font-medium mb-4">
                        Create your first form with AI in seconds.
                    </p>
                    <button
                        onClick={() => navigate("/form-builder/create")}
                        className="px-6 py-2 rounded-lg text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                        Create Form
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {forms.map((form) => (
                        <div
                            key={form.formId}
                            className="border-[1px] dark:border-gray-700 rounded-lg dark:bg-[#23263a] shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                        >
                            {/* Color strip */}
                            <div
                                className="h-1.5"
                                style={{
                                    backgroundColor:
                                        form.theme?.primaryColor || "#6366f1",
                                }}
                            />
                            <div className="p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 min-w-0">
                                        {editingTitle === form.formId ? (
                                            <input
                                                type="text"
                                                value={editTitleValue}
                                                onChange={(e) => setEditTitleValue(e.target.value)}
                                                onBlur={() => handleTitleSave(form.formId)}
                                                onKeyDown={(e) => handleTitleKeyDown(e, form.formId)}
                                                autoFocus
                                                maxLength={200}
                                                className="text-lg font-semibold w-full bg-transparent border-b-2 border-indigo-500 outline-none py-0"
                                            />
                                        ) : (
                                            <h3
                                                className="text-lg font-semibold truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400"
                                                onClick={() => handleTitleEdit(form)}
                                                title="Click to edit title"
                                            >
                                                {form.title}
                                            </h3>
                                        )}
                                        {form.description && (
                                            <p className="text-sm text-gray-500 dark:text-gray-400 font-normal truncate">
                                                {form.description}
                                            </p>
                                        )}
                                    </div>
                                    <span
                                        className={`text-xs px-2 py-0.5 rounded ml-2 flex-shrink-0 ${
                                            form.isPublished
                                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                                        }`}
                                    >
                                        {form.isPublished ? "Published" : "Draft"}
                                    </span>
                                </div>

                                <div className="flex gap-4 text-sm text-gray-400 dark:text-gray-500 font-normal mb-3">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">
                                        {form.responseCount || 0} responses
                                    </span>
                                    <span>{form.fields?.length || 0} fields</span>
                                    <span>
                                        {form.updatedAt
                                            ? new Date(form.updatedAt).toLocaleDateString()
                                            : ""}
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        onClick={() => navigate(`/form-builder/edit/${form.formId}`)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => navigate(`/form-builder/${form.formId}/responses`)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Responses
                                    </button>
                                    <button
                                        onClick={() => navigate(`/form-builder/${form.formId}/insights`)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Insights
                                    </button>
                                    <button
                                        onClick={() => navigate(`/form-builder/${form.formId}/workflows`)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Workflows
                                    </button>
                                    <button
                                        onClick={() => navigate(`/form-builder/${form.formId}/recipes`)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Recipes
                                    </button>
                                    <button
                                        onClick={() => copyLink(form.formId)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Copy Link
                                    </button>
                                    <button
                                        onClick={() => copyEmbed(form.formId)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        Embed
                                    </button>
                                    <button
                                        onClick={() => handleTogglePublish(form.formId, form.isPublished)}
                                        className="px-3 py-1 text-sm border-[1px] dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-[#2c2f44]"
                                    >
                                        {form.isPublished ? "Unpublish" : "Publish"}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(form.formId)}
                                        className="px-3 py-1 text-sm border-[1px] rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 dark:border-gray-600"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Upgrade CTA — canonical plans live at /settings/plans */}
            {effectivePlan !== "business" && effectivePlan !== "agency" && (
                <div className="mt-12 mb-10 rounded-2xl border border-indigo-200 dark:border-indigo-700/50 bg-indigo-50/60 dark:bg-indigo-900/15 p-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold">Need more responses, AI generations or agent workflows?</h2>
                        <p className="text-gray-500 dark:text-gray-400 font-medium mt-1">
                            Compare every plan's limits and upgrade in a click.
                        </p>
                    </div>
                    <Link
                        to="/settings/plans"
                        className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold no-underline whitespace-nowrap"
                    >
                        View plans &amp; upgrade →
                    </Link>
                </div>
            )}

            {error && (
                <p className="mt-3 text-red-600 dark:text-red-400 font-medium">
                    {error}
                </p>
            )}

            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={localStorage.getItem("theme") === "dark" ? "dark" : "light"}
            />
        </div>
    );
}

export default FormBuilder;
